import { UseQueryResult } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { IDetailsTeamResponse } from "../../../../models/endpoints/Team";
import { Grid, GridItem, Skeleton } from "@chakra-ui/react";
import style from "./TablesRow.module.scss";
import Typhography from "../../../../shared/Typhography/Typhography";
import { ITeam } from "../../../../models/Team";
import {
  addBaseUrl,
  getLabelFromOptions,
} from "../../../../lib/DurationPicker/utils";
import { ROLES_SPORT_OPTIONS } from "../../../../constants/config";
import CustomAvatar from "../../../../shared/CustomAvatar/CustomAvatar";
import classNames from "classnames";
import { formatFullName } from "../../../../utils/functions";

export const TablesRow: FC<{
  query: UseQueryResult<IDetailsTeamResponse, Error>;
  teamQuery: UseQueryResult<ITeam, Error>;
}> = ({ query, teamQuery }) => {
  const { topRecoveries = [], bottomRecoveries = [], senzaDato = 0 } = useMemo(() => {
    if (!query.data) return {};

    /**
     * Classifica solo su chi ha un dato.
     *
     * Prima si leggeva `recovery[0]?.interpolation || 0`: chi non aveva mai
     * compilato entrava in classifica a 0% e finiva sia fra i "peggiori"
     * (falsa allerta su un atleta che sta benissimo) sia fra i "migliori"
     * quando il team era quasi tutto senza dati. Il backend ora manda
     * `currentRecovery: null` proprio per poterli distinguere.
     */
    const conDato = query.data.readinessByUser.filter(
      (user) => typeof user.currentRecovery === "number"
    ) as (typeof query.data.readinessByUser[number] & {
      currentRecovery: number;
    })[];

    const ordinati = [...conDato].sort(
      (a, b) => b.currentRecovery - a.currentRecovery
    );

    /**
     * Con pochi atleti le due liste si sovrapporrebbero (gli stessi tre nomi
     * come migliori e come peggiori). Sotto i sei si mostra solo la classifica
     * dei migliori, che è già l'intero quadro.
     */
    return {
      topRecoveries: ordinati.slice(0, 3),
      /**
       * Dal peggiore in giù. `slice(-3)` li lasciava in ordine decrescente,
       * quindi nel pannello "Peggior Recupero" il caso più critico finiva in
       * ULTIMA riga — mentre l'occhio legge la prima come la più importante.
       */
      bottomRecoveries:
        ordinati.length >= 6 ? ordinati.slice(-3).reverse() : [],
      senzaDato: query.data.readinessByUser.length - conDato.length,
    };
  }, [query.data]);

  return (
    <Grid
      templateRows="repeat(1, 1fr)"
      templateColumns="repeat(2, 1fr)"
      gap={4}
    >
      {[
        {
          title: "Miglior Recupero",
          data: topRecoveries,
        },
        // Nascosta finché non ci sono abbastanza atleti da rendere le due
        // classifiche davvero diverse: altrimenti sarebbero gli stessi nomi.
        ...(bottomRecoveries.length
          ? [{ title: "Peggior Recupero", data: bottomRecoveries }]
          : []),
      ].map((item, index) => {
        return (
          <GridItem colSpan={1} key={item.title}>
            <Skeleton loading={query.isPending}>
              <div className={style.container}>
                <Typhography size="bold-14">{item.title}</Typhography>
                <div className={style.containerBody}>
                  {item.data.length === 0 && (
                    <Typhography size="regular-12" className={style.roleText}>
                      Nessun atleta ha ancora un indice di recupero.
                    </Typhography>
                  )}
                  {item.data?.map((user, i) => {
                    return (
                      <div className={style.containerItem} key={user._id}>
                        <div className="d-flex align-items-center w-100 gap-1">
                          {index === 0 && (
                            <div className={style.containerCounter}>
                              <Typhography size="bold-10">{i + 1}°</Typhography>
                            </div>
                          )}
                          <CustomAvatar
                            key={user._id}
                            name={`${user.firstName[0]}${user.lastName[0]}`}
                            src={addBaseUrl(1, user.image?.image)}
                          />
                          <div className="d-flex flex-column flex-1">
                            <div className="d-flex align-items-center justify-content-between">
                              <Typhography size="bold-12">
                                {formatFullName(user.firstName, user.lastName)}
                              </Typhography>
                              <div
                                className={classNames(style.containerRecovery, {
                                  [style.badRecovery]: index === 1,
                                })}
                              >
                                <Typhography size="medium-12">
                                  {user.currentRecovery}%
                                </Typhography>
                              </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center gap-1">
                                {(user as any).roles?.map?.((role: string) => {
                                  return (
                                    <Typhography
                                      key={role}
                                      size="medium-12"
                                      className={style.roleText}
                                    >
                                      {getLabelFromOptions(
                                        ROLES_SPORT_OPTIONS[
                                          (teamQuery.data?.spaceId as any)
                                            .type as any as keyof typeof ROLES_SPORT_OPTIONS
                                        ],
                                        role
                                      )}
                                    </Typhography>
                                  );
                                })}
                              </div>
                              <Typhography
                                size="regular-12"
                                className={style.roleText}
                              >
                                Indice di recupero
                              </Typhography>
                            </div>
                          </div>
                        </div>
                        {/* <div className="d-flex gap-1 align-items-center">
                    <div className="d-flex flex-column">
                      <div className={style.containerRecovery}>
                        <Typhography size="medium-12">
                          {user.currentRecovery}
                        </Typhography>
                      </div>
                      <Typhography size="medium-12">
                        Indice di recupero
                      </Typhography>
                    </div>
                  </div> */}
                      </div>
                    );
                  })}
                  {index === 0 && senzaDato > 0 && (
                    <Typhography size="regular-12" className={style.roleText}>
                      {senzaDato === 1
                        ? "1 atleta non ha ancora compilato: escluso dalla classifica."
                        : `${senzaDato} atleti non hanno ancora compilato: esclusi dalla classifica.`}
                    </Typhography>
                  )}
                </div>
              </div>
            </Skeleton>
          </GridItem>
        );
      })}
    </Grid>
  );
};
